import {
    addChannelMessage,
    getChannelInfo,
    recalculateChannelMessages,
    setActiveConversationChannel,
    setChannelHasMore,
    setChannelUnreadCount,
    setRecentChannelUnreadRecords,
    overwriteChannelLastReadMessage,
    updateMessageReaction,
    updateUnreadSeparatorLabel,
    upsertRecentChannelItem,
} from '../../../slice';
import { Store } from '../../../store';
import { navigateChannel } from '../../../shell/Sidebar';
import { NOTIFICATION_TYPES, sendChannelEventNotification } from '../notificationsService/channelNotificationService';
import logger from '../../../Helpers/logger';

// Configuration
const MAX_UNREAD_MESSAGES_LIMIT = 20;

// Helper: Transform incoming message data
const transformMessageData = messageData => {
    return {
        ...messageData,
        createdAt: new Date(messageData.messageTime).toISOString(),
        updatedAt: new Date(messageData.messageTime).toISOString(),
        editedAt: null,
        deletedAt: null,
        meta: null,
    };
};

// Helper: Check if channel is under unread message limit
const checkUnreadMessageLimit = (channelId, maxUnreadMessages = MAX_UNREAD_MESSAGES_LIMIT) => {
    const state = Store.getState();
    const channel = state.channelConversationData?.channelConversations?.[channelId];

    if (!channel?.messages) return true;

    const unreadSeparatorIndex = channel.messages.findIndex(m => m.type === 'unread-separator');

    // No separator means no unread messages yet, allow the message
    if (unreadSeparatorIndex === -1) return true;

    // Count messages before the separator (newer messages are at lower indices)
    const currentUnreadCount = unreadSeparatorIndex;

    return currentUnreadCount < maxUnreadMessages;
};

// Helper: Get channel UI state
const getChannelUiState = channelId => {
    return Store.getState()?.channelConversationData?.channelUiState?.[channelId];
};

// Helper: Check if unread separator exists in channel
const hasUnreadSeparator = channelId => {
    const state = Store.getState();
    const channel = state.channelConversationData?.channelConversations?.[channelId];

    if (!channel?.messages) return false;

    return channel.messages.some(m => m.type === 'unread-separator');
};

// Handle recent channel list update
const updateRecentChannelList = (channelId, messageData) => {
    Store.dispatch(
        upsertRecentChannelItem({
            channelId: channelId,
            lastActivityAt: messageData.messageTime,
            newIndex: 0,
            lastRecentMessage: messageData,
        }),
    );
};

// Handle unread count updates
const handleUnreadCountUpdate = (messageData, isViewing, increaseUnreadCount, channelInfo) => {
    if (!messageData.unreadCount || isViewing) return;

    const existingLastReadMessageId = channelInfo?.lastReadMessage?.messageId;

    Store.dispatch(
        setChannelUnreadCount({
            channelId: messageData.channelId,
            unreadCount: increaseUnreadCount ? 0 : messageData.unreadCount,
            messageId: existingLastReadMessageId || messageData.messageId,
        }),
    );

    if (channelInfo && channelInfo.unreadCount === 0 && !increaseUnreadCount) {
        const unreadRecords = Store.getState().recentChannelListData.unreadRecords;
        Store.dispatch(setRecentChannelUnreadRecords(unreadRecords + 1));
    }
};

// Handle channel notifications
const scheduleChannelNotification = (messageData, channelInfo) => {
    setTimeout(
        () => {
            const updatedChannelInfo = getChannelInfo(messageData.channelId);
            if (updatedChannelInfo) {
                // Use the new notification service with MESSAGE type
                sendChannelEventNotification(
                    NOTIFICATION_TYPES.MESSAGE,
                    {
                        channelName: updatedChannelInfo.channelName || updatedChannelInfo.name,
                        channelId: messageData.channelId,
                        channel: updatedChannelInfo,
                        senderId: messageData.senderId, // Pass senderId for validation
                        messageData: messageData, // Pass full messageData for validation
                    },
                    {
                        channelId: messageData.channelId,
                        onClick: () => {
                            if (navigateChannel?.current) {
                                navigateChannel.current.toRecentChannels();
                            }
                            setTimeout(() => {
                                Store.dispatch(setActiveConversationChannel({ channelId: messageData.channelId }));
                            }, 10);
                        },
                    },
                );
            }
        },
        channelInfo ? 0 : 3000,
    );
};

// Handle unread separator recalculation
// Returns true if message insertion should be skipped
const handleUnreadSeparatorRecalculation = (channelId, isViewing, channelInfo) => {
    if (isViewing) return;

    const lastReadMessageId = channelInfo?.lastReadMessage?.messageId;
    const unreadCount = channelInfo?.unreadCount || 0;

    if (unreadCount > 0 && lastReadMessageId) {
        Store.dispatch(
            recalculateChannelMessages({
                channelId: channelId,
                lastReadMessageId: lastReadMessageId,
                unreadCount: unreadCount,
            }),
        );
    }
};

// Handle message insertion with limit check
const handleMessageInsertion = (channelId, transformedMessage, isViewing) => {
    const shouldAcceptMessage = isViewing || checkUnreadMessageLimit(channelId, MAX_UNREAD_MESSAGES_LIMIT);

    if (shouldAcceptMessage) {
        Store.dispatch(
            addChannelMessage({
                channelId: channelId,
                message: transformedMessage,
            }),
        );
    } else {
        // Block further messages by setting hasMoreForward to true
        Store.dispatch(
            setChannelHasMore({
                channelId: channelId,
                direction: 'forward',
                value: true,
            }),
        );
        logger.debug(
            `Blocked message for channel ${channelId}: Unread limit reached (${MAX_UNREAD_MESSAGES_LIMIT} messages)`,
        );
    }
};

// Main handler
export const handleChannelIncomingMessage = messageData => {
    try {
        const transformedMessage = transformMessageData(messageData);
        const isViewing = isUserViewingChannel(transformedMessage.channelId);
        const channelUiState = getChannelUiState(transformedMessage.channelId);
        const hasMoreForward = channelUiState?.hasMoreForward;
        const hasInitialized = channelUiState?.initialized;
        const increaseUnreadCount = isViewing && !hasMoreForward;
        updateRecentChannelList(messageData.channelId, messageData);

        // If the server sent a lastRecentMessage, overwrite channel's lastRecentMessage
        if (messageData.content) {
            Store.dispatch(
                overwriteChannelLastReadMessage({ channelId: messageData.channelId, lastRecentMessage: messageData }),
            );
        }

        // Get channel info
        const channelInfo = getChannelInfo(messageData.channelId);
        if (isViewing) {
            handleChannelReadCountResetEvent({ channelId: messageData.channelId });
        }
        // Handle unread count
        handleUnreadCountUpdate(messageData, isViewing, increaseUnreadCount, channelInfo);

        // Schedule notification
        scheduleChannelNotification(messageData, channelInfo);

        logger.debug('message insert condition ==>', { hasMoreForward, hasInitialized });

        // Handle message insertion if conditions are met
        if (!hasMoreForward && hasInitialized) {
            // Recalculate unread separator if needed
            handleUnreadSeparatorRecalculation(messageData.channelId, isViewing, channelInfo);

            // Insert message with limit check
            handleMessageInsertion(messageData.channelId, transformedMessage, isViewing);
        }
    } catch (error) {
        logger.debug('Failed to handle the incoming message', error);
    }
};

export const handleReactionEvent = data => {
    try {
        const { channelId, messageId, reactions } = data;

        Store.dispatch(updateMessageReaction({ channelId, messageId, reactions }));
    } catch (error) {
        logger.debug('Failed to update the reactions', error);
    }
};

/**
 * Check if user is currently viewing the channel
 */
const isUserViewingChannel = channelId => {
    try {
        const activeChannel = Store.getState().channelNavData?.activeConversationChannel;
        return activeChannel?.channelId === channelId;
    } catch (error) {
        logger.debug('Failed to check if user is viewing channel:', error);
        return false;
    }
};

const handleChannelReadCountResetEvent = ({ channelId }) => {
    try {
        const hasUnreadLabel = hasUnreadSeparator(channelId);
        const isViewing = isUserViewingChannel(channelId);
        // If user is viewing and has unread separator, only update the label count
        if (isViewing && hasUnreadLabel) {
            Store.dispatch(
                updateUnreadSeparatorLabel({
                    channelId: channelId,
                }),
            );
        }
    } catch (error) {
        logger.debug('Failed to handle the channel read count reset event', error);
    }
};
